<template>
  <div v-if="!mobileAllowed" class="mobile-block-page">
    <van-nav-bar title="印章相似度测试" left-arrow @click-left="handleBack" />
    <div class="mobile-block-body">
      <p class="mobile-block-title">请使用手机访问</p>
      <p class="mobile-block-desc">印章相似度测试仅支持移动端，请在手机浏览器或微信中打开。</p>
    </div>
  </div>
  <div v-else class="seal-test-container">
    <van-nav-bar title="印章相似度测试" left-arrow @click-left="handleBack" />
    
    <div class="content">
      <!-- 规则选择 -->
      <div class="form-group">
        <van-field
          v-model="selectedRuleRemark"
          label="选择规则"
          placeholder="请选择印章规则"
          readonly
          is-link
          @click="showRulePicker = true"
        />
        <van-popup v-model="showRulePicker" position="bottom" get-container="body" class="seal-picker-popup">
          <van-picker
            show-toolbar
            title="选择规则"
            :columns="ruleColumns"
            :default-index="ruleDefaultIndex"
            :item-height="44"
            @confirm="onRuleConfirm"
            @cancel="showRulePicker = false"
          />
        </van-popup>
      </div>

      <!-- 调用门店编码（持久化到本地，刷新不丢失） -->
      <div class="form-group form-group--input">
        <label>门店编码</label>
        <el-input
          v-model="authStoreCode"
          placeholder="请输入门店编码"
          clearable
          @input="saveAuthStoreCodeToCache"
        />
      </div>

      <!-- 盖章区域 -->
      <div class="form-group">
        <van-field
          :value="areaPreset.label"
          label="盖章区域"
          placeholder="请选择盖章区域"
          readonly
          is-link
          @click="showAreaPicker = true"
        />
        <van-popup v-model="showAreaPicker" position="bottom" get-container="body" class="seal-picker-popup">
          <van-picker
            :key="'area-picker-' + areaId"
            show-toolbar
            title="选择盖章区域"
            :columns="areaColumns"
            :default-index="areaDefaultIndex"
            :item-height="44"
            @confirm="onAreaConfirm"
            @cancel="showAreaPicker = false"
          />
        </van-popup>
        <p class="area-hint">{{ areaPreset.hint }}（coordinate_system={{ areaPreset.coordinateSystem }}）</p>
      </div>

      <!-- 印章绘制区域：固定比例 -->
      <div
        v-if="!isFullscreenMode"
        class="canvas-container seal-canvas-fullbleed"
        :style="canvasWrapperStyle"
      >
        <canvas
          ref="canvas"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        ></canvas>
        <p class="hint">在 {{ areaPreset.label }} 区域内触摸屏幕记录触点（4 个触点将进行相似度对比）</p>
      </div>

      <!-- 全屏坐标系：弹层录入 -->
      <div v-else class="fullscreen-entry-section">
        <van-button type="primary" block round @click="openFullscreenTouch">
          进入全屏测试
        </van-button>
        <p class="hint fullscreen-hint">全屏模式下坐标归一化为 0–10000，与屏幕尺寸无关</p>
      </div>

      <seal-fullscreen-touch
        v-model="showFullscreenTouch"
        :area-id="areaId"
        title="全屏相似度测试"
        label="四指同时按下，松手后自动比对"
        @confirm="onFullscreenTouchConfirm"
      />

      <!-- 点位显示 -->
      <div v-if="touchPoints.length > 0" class="points-info">
        <p><strong>已绘制点位 ({{ touchPoints.length }}):</strong></p>
        <p v-for="(point, idx) in touchPoints" :key="idx">
          点{{ idx + 1 }}: [{{ point[0] }}, {{ point[1] }}]（{{ areaPreset.label }}）
        </p>
      </div>

      <!-- 操作按钮 -->
      <div class="button-group">
        <van-button type="primary" @click="compareSeals" :disabled="touchPoints.length === 0 || !currentRule">
          {{ touchPoints.length === 4 ? '对比相似度' : '记录数据' }}
        </van-button>
        <van-button @click="clearCanvas">清空画布</van-button>
      </div>

      <!-- 对比结果弹窗：仅展示是否匹配成功 -->
      <el-dialog
        title="对比结果"
        :visible.sync="showResultDialog"
        width="90%"
        @close="handleDialogClose"
      >
        <div v-if="comparisonResult" class="result-dialog-content">
          <div class="result-item">
            <span class="label">匹配结果:</span>
            <span :class="(comparisonResult.valid || comparisonResult.matched) ? 'matched' : 'not-matched'">
              {{ (comparisonResult.valid || comparisonResult.matched) ? '✓ 匹配成功' : '✗ 匹配失败' }}
            </span>
          </div>
          <div
            class="result-item"
            v-if="!(comparisonResult.valid || comparisonResult.matched) && (comparisonResult.errorMessage || comparisonResult.message)"
          >
            <span class="label">说明:</span>
            <span class="value">{{ comparisonResult.errorMessage || comparisonResult.message }}</span>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showResultDialog = false">关闭</el-button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { getSealRules, compareSealPointsAct004 } from '@/api/sealTest'
import {
  DEFAULT_SEAL_AREA_ID,
  SEAL_AREA_PRESETS,
  getSealAreaPreset,
  mapTouchToStandard,
  buildSealPointsSubmitPayload,
  isSealFullscreenMode
} from '@/pages/utils/seal-area-presets'
import { sealMobileOnlyMixin, syncSealCanvasSize, resolveVanPickerOption, formatSealRuleLabel } from '@/pages/utils/seal-mobile-only'
import SealFullscreenTouch from '@/pages/components/SealFullscreenTouch.vue'

export default {
  name: 'SealTest',
  components: { SealFullscreenTouch },
  mixins: [sealMobileOnlyMixin],
  data() {
    return {
      ruleList: [],
      allRules: [],
      ruleColumns: [],
      selectedRuleId: null,
      selectedRuleRemark: '',
      currentRule: null,
      showRulePicker: false,
      showRuleList: false,
      /** 调用门店编码，从 localStorage 读取，输入时写回缓存 */
      authStoreCode: '',
      touchPoints: [],
      isDrawing: false,
      comparisonResult: null,
      showResultDialog: false,
      canvas: null,
      ctx: null,
      areaId: DEFAULT_SEAL_AREA_ID,
      areaOptions: SEAL_AREA_PRESETS,
      areaColumns: SEAL_AREA_PRESETS.map((item) => ({ text: item.label, value: item.id })),
      showAreaPicker: false,
      showFullscreenTouch: false,
      // 防抖定时器
      debounceTimer: null
    }
  },
  computed: {
    areaPreset() {
      return getSealAreaPreset(this.areaId)
    },
    isFullscreenMode() {
      return isSealFullscreenMode(this.areaId)
    },
    canvasWrapperStyle() {
      const ar = this.areaPreset.aspectRatio
      return ar ? { '--seal-aspect-ratio': ar } : {}
    },
    areaDefaultIndex() {
      const idx = this.areaOptions.findIndex((item) => item.id === this.areaId)
      return idx >= 0 ? idx : 0
    },
    ruleDefaultIndex() {
      if (!this.selectedRuleId) return 0
      const idx = this.ruleColumns.findIndex((item) => item.value === this.selectedRuleId)
      return idx >= 0 ? idx : 0
    }
  },
  mounted() {
    if (!this.mobileAllowed) return
    this.initCanvas()
    this.authStoreCode = (typeof localStorage !== 'undefined' && localStorage.getItem('sealTest_authStoreCode')) || ''
    this.loadRules()
  },
  beforeDestroy() {
    // 组件销毁时清除定时器，避免内存泄漏
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  },
  methods: {
    initCanvas() {
      this.$nextTick(() => {
        this.canvas = this.$refs.canvas
        this.syncCanvasSize()
        window.setTimeout(() => this.syncCanvasSize(), 80)
      })
    },
    syncCanvasSize() {
      syncSealCanvasSize(this)
    },
    onAreaChange() {
      this.showFullscreenTouch = false
      this.clearCanvas()
      if (!this.isFullscreenMode) {
        this.initCanvas()
      }
    },
    openFullscreenTouch() {
      if (!this.currentRule) {
        this.$toast.fail('请先选择规则')
        return
      }
      this.showFullscreenTouch = true
    },
    onFullscreenTouchConfirm(points) {
      this.showFullscreenTouch = false
      this.touchPoints = points
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = null
      }
      this.debounceTimer = setTimeout(() => {
        this.compareSeals()
        this.debounceTimer = null
      }, 300)
    },
    onAreaConfirm(payload) {
      const nextId = resolveVanPickerOption(payload)
      if (nextId && nextId !== this.areaId) {
        this.areaId = nextId
        this.onAreaChange()
      }
      this.showAreaPicker = false
    },
    async loadRules() {
      try {
        const res = await getSealRules()
        const rules = res.data || res
        this.allRules = rules
        this.ruleColumns = rules.map((rule) => ({
          text: formatSealRuleLabel(rule),
          value: rule.ruleCode,
          ruleCode: rule.ruleCode,
          remark: rule.remark
        }))
      } catch (err) {
        console.error('加载规则失败:', err)
        this.$toast.fail('加载规则失败')
      }
    },
    onRuleConfirm(payload) {
      const ruleCode = resolveVanPickerOption(payload)
      const rule = this.allRules.find((r) => r.ruleCode === ruleCode)
      if (!rule || !ruleCode) {
        this.$toast.fail('请选择有效规则')
        return
      }
      this.selectedRuleId = ruleCode
      this.selectedRuleRemark = formatSealRuleLabel(rule)
      this.currentRule = rule
      this.showRulePicker = false
    },
    /** 将 authStoreCode 写入 localStorage，刷新页面不丢失 */
    saveAuthStoreCodeToCache() {
      if (typeof localStorage === 'undefined') return
      const val = (this.authStoreCode && this.authStoreCode.trim()) || ''
      if (val) {
        localStorage.setItem('sealTest_authStoreCode', val)
      } else {
        localStorage.removeItem('sealTest_authStoreCode')
      }
    },
    handleTouchStart(e) {
      e.preventDefault()
      if (!this.canvas || !this.ctx || e.touches.length === 0) return
      this.syncCanvasSize()
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []
        
        const rect = this.canvas.getBoundingClientRect()
        const scaleX = this.canvas.width / rect.width
        const scaleY = this.canvas.height / rect.height

        for (let i = 0; i < e.touches.length; i++) {
          const touch = e.touches[i]
          const mapped = mapTouchToStandard(touch, rect, this.areaId)
          const canvasX = mapped.displayX * scaleX
          const canvasY = mapped.displayY * scaleY

          this.touchPoints.push([mapped.standardX, mapped.standardY])
          this.drawPoint(canvasX, canvasY, i + 1)
        }

        console.log(`${e.touches.length}个触点，区域 ${this.areaPreset.label}，点位记录:`, this.touchPoints)

        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
        }
        
        // 设置新的定时器，1秒后调用接口
        this.debounceTimer = setTimeout(() => {
          this.compareSeals()
          this.debounceTimer = null
        }, 1000)
    },
    handleTouchEnd(e) {
      // 手指离开时，如果还有定时器，立即执行接口调用（不再等待）
      if (e.touches.length === 0) {
        console.log('所有手指离开')
        // 如果还有待执行的定时器，立即执行并清除
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
          this.debounceTimer = null
          // 如果有点位数据，立即调用接口
          if (this.touchPoints.length > 0) {
            this.compareSeals()
          }
        }
      }
    },
    drawPoint(x, y, index) {
      // 绘制红色圆点
      this.ctx.fillStyle = '#FF0000'
      this.ctx.beginPath()
      this.ctx.arc(x, y, 8, 0, 2 * Math.PI)
      this.ctx.fill()
      
      // 绘制序号文字
      this.ctx.fillStyle = '#FFFFFF'
      this.ctx.font = 'bold 12px Arial'
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'middle'
      this.ctx.fillText(index.toString(), x, y)
    },
    clearCanvas() {
      if (this.canvas && this.ctx) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }
      this.touchPoints = []
      this.comparisonResult = null
    },
    async compareSeals() {
      console.log(this.touchPoints, this.currentRule)
      if (this.touchPoints.length === 0 || !this.currentRule) {
        this.$toast.fail('请选择规则并触摸屏幕')
        return
      }

      try {
        const submitPayload = buildSealPointsSubmitPayload(this.touchPoints, this.areaId)
        const res = await compareSealPointsAct004({
          ruleCode: this.currentRule.ruleCode,
          touchPoints: submitPayload.points,
          authStoreCode: this.authStoreCode,
          coordinateSystem: submitPayload.coordinateSystem
        })
        console.log('接口结果:', res)
        // 响应仅关心 matched / errorMessage（得分与模版点位已脱敏不回传）
        if (res && res.data) {
          this.comparisonResult = res.data
        } else if (res && (res.valid !== undefined || res.matched !== undefined)) {
          this.comparisonResult = res
        } else {
          this.comparisonResult = res
        }
        console.log('最终结果:', this.comparisonResult)
        
        // 如果是4个触点，显示对比结果弹窗；否则只提示记录成功
        if (this.touchPoints.length === 4) {
          this.showResultDialog = true
        } else {
          this.$toast.success(`已记录 ${this.touchPoints.length} 个触点数据`)
        }
      } catch (err) {
        console.error('接口调用失败:', err)
        this.$toast.fail('操作失败: ' + (err.message || ''))
      }
    },
    handleDialogClose() {
      this.showResultDialog = false
      this.clearCanvas()
    },
    handleBack() {
      this.$router.push('/sealNav')
    }
  }
}
</script>

<style scoped>
.mobile-block-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.mobile-block-body {
  padding: 48px 24px;
  text-align: center;
}

.mobile-block-title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.mobile-block-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}

.seal-test-container {
  background: #f5f5f5;
  min-height: 100vh;
}

.content {
  padding: 16px;
}

.form-group {
  background: white;
  padding: 0;
  margin: 12px 0;
  border-radius: 4px;
  overflow: hidden;
}

.form-group ::v-deep .van-field__control {
  line-height: 24px;
  min-height: 24px;
}

.form-group .area-hint {
  padding: 0 16px 12px;
}

.form-group--input {
  padding: 16px;
}

.form-group--input label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

.content {
  padding: 16px 16px 24px;
}

.seal-canvas-fullbleed {
  width: 100vw;
  margin: 12px 0 0;
  margin-left: calc(50% - 50vw);
  padding: 0;
  background: #fff;
}

.seal-canvas-fullbleed canvas {
  display: block;
  width: 100%;
  height: auto;
  max-width: none;
  border: none;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  border-radius: 0;
  touch-action: none;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  aspect-ratio: var(--seal-aspect-ratio, 800 / 600);
}

.fullscreen-entry-section {
  background: #fff;
  padding: 16px;
  margin: 12px 0;
  border-radius: 4px;
}

.fullscreen-hint {
  margin: 12px 0 0;
  text-align: center;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}

.area-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #999;
}

.rule-info, .points-info, .result-info {
  background: white;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  font-size: 14px;
}

.button-group {
  display: flex;
  gap: 8px;
  margin: 16px 0;
}

.button-group button {
  flex: 1;
}

.matched {
  color: #07c160;
}

.not-matched {
  color: #ff0000;
}
</style>

<style lang="less">
.seal-picker-popup .van-picker-column__item {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
  padding: 0 12px;
  box-sizing: border-box;
}
</style>

